import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useQuery, gql } from '@apollo/client';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../../graphql/queries/getChannelAll';
import { WmsProgressFilterInput } from '../../graphql/queries/getWmsProgress';
import { useTranslation } from 'react-i18next';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import DateTimeRangeFilterItem from '../../../../delivery/components/DateTimeRangeFilterItem';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';

  const GET_CARRIER_OPTIONS = gql`
    query GetCarrierOptions($includeSwiftX: Boolean!) {
      getCarrierOptions(includeSwiftX: $includeSwiftX) {
        value
        name
      }
    }
  `;

interface WmsProgressFilterProps {
  filters: WmsProgressFilterInput;
  setFilters: (filters: WmsProgressFilterInput) => void;
  handleFilter: () => void;
  handleResetFilters: () => void;
}

const WmsProgressFilter: React.FC<WmsProgressFilterProps> = ({
  filters,
  setFilters,
  handleFilter,
  handleResetFilters,
}) => {
  const { t } = useTranslation();
  const [channelOptions, setChannelOptions] = useState<{ value: string; label: string }[]>([]);
  const [carrierOptions, setCarrierOptions] = useState<{ value: string; label: string }[]>([]);
  
  // 默认时间为一周
  const getDefaultTimeRange = (): [Dayjs, Dayjs] => {
    const end = dayjs().minute(0).second(0).millisecond(0);
    const start = end.subtract(7, 'day').minute(0).second(0).millisecond(0);
    return [start, end];
  };

  // 从filters中获取日期范围值，转换为DateTimeRangeFilterItem需要的格式
  const getDateRangeValue = (): [string | null, string | null] | null => {
    if (filters.arrivedCurrentWarehouseDateRange) {
      try {
        return [
          filters.arrivedCurrentWarehouseDateRange.start,
          filters.arrivedCurrentWarehouseDateRange.end
        ];
      } catch (e) {
        return null;
      }
    }
    // 如果没有值，返回默认24小时范围
    const [start, end] = getDefaultTimeRange();
    return [start.format('YYYY-MM-DD HH:mm:ss'), end.format('YYYY-MM-DD HH:mm:ss')];
  };
  
  const { data } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
  const { data: carrierData } = useQuery(GET_CARRIER_OPTIONS, {
    variables: { includeSwiftX: true }
  });

  useEffect(() => {
    if (data?.getAllChannels) {
      const channels = Array.isArray(data.getAllChannels) ? data.getAllChannels : [data.getAllChannels];
      const options = channels.map((channel) => ({
        value: channel.id.toString(),
        label: channel.channelName
      }));
      setChannelOptions(options);
    }
  }, [data]);

  useEffect(() => {
    if (carrierData?.getCarrierOptions) {
      const options = carrierData.getCarrierOptions.map((carrier: any) => ({
        value: carrier.value.toString(),
        label: carrier.name
      }));
      setCarrierOptions(options);
    }
  }, [carrierData]);

  // 初始化时如果没有时间范围，设置默认一周范围
  useEffect(() => {
    if (!filters.arrivedCurrentWarehouseDateRange) {
      const [start, end] = getDefaultTimeRange();
      setFilters({
        ...filters,
        arrivedCurrentWarehouseDateRange: {
          start: start.format('YYYY-MM-DD HH:mm:ss'),
          end: end.format('YYYY-MM-DD HH:mm:ss')
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.currentWarehouseId]);

  // 处理时间变化
  const handleDateRangeChange = (dates: [string | null, string | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({
        ...filters,
        arrivedCurrentWarehouseDateRange: {
          start: dates[0],
          end: dates[1]
        }
      });
    } else {
      setFilters({
        ...filters,
        arrivedCurrentWarehouseDateRange: undefined
      });
    }
  };

  // 处理渠道变化
  const handleChannelChange = (value: string | undefined) => {
    setFilters({
      ...filters,
      channelId: value ? Number(value) : undefined
    });
  };

  // 处理尾程商变化
  const handleCarrierChange = (value: string | undefined) => {
    setFilters({
      ...filters,
      carrierCode: value ? Number(value) : undefined
    });
  };

  // 处理重置
  const handleReset = () => {
    // 先调用父组件的重置函数，它会重置所有 filters 只保留 currentWarehouseId
    handleResetFilters();
    // 然后设置默认时间范围
    const [start, end] = getDefaultTimeRange();
    setFilters({
      currentWarehouseId: filters.currentWarehouseId,
      arrivedCurrentWarehouseDateRange: {
        start: start.toISOString(),
        end: end.toISOString()
      },
      channelId: undefined,
      carrierCode: undefined
    });
  };

  const col1SpanClassName = getSmartLabelClassName([t('wms.progress.filters.arrivalTime')]);
  const col2SpanClassName = getSmartLabelClassName([t('wms.progress.filters.clientName')]);
  const col3SpanClassName = getSmartLabelClassName([t('wms.progress.filters.carrier')]);

  return (
    <div className="mb-2 pl-0 py-2">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* 筛选条件 - 使用 Grid 布局确保列对齐，第一列稍微宽一点以适应时间范围 */}
        <div className="flex-1">
          <div className="grid gap-x-10 gap-y-4" style={{ gridTemplateColumns: '1.15fr 1fr 1fr' }}>
            <div className="min-w-0">
              <DateTimeRangeFilterItem
                label={t('wms.progress.filters.arrivalTime')}
                value={getDateRangeValue()}
                onChange={handleDateRangeChange}
                className="w-full"
                spanClassName={col1SpanClassName}
                format="YYYY/MM/DD HH:mm"
                showTime={{
                  format: 'HH:mm',
                  defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
                }}
                size="middle"
              />
            </div>

            <div className="min-w-0">
              <ParcelFilterStyle
                label={t('wms.progress.filters.clientName')}
                options={channelOptions}
                value={filters.channelId?.toString() || undefined}
                onChange={handleChannelChange}
                className="w-full"
                spanClassName={col2SpanClassName}
              />
            </div>

            <div className="min-w-0">
              <ParcelFilterStyle
                label={t('wms.progress.filters.carrier')}
                options={carrierOptions}
                value={filters.carrierCode?.toString() || undefined}
                onChange={handleCarrierChange}
                className="w-full"
                spanClassName={col3SpanClassName}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WmsProgressFilter;